import { StyledEngineProvider, Theme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import App from './App';
import AccountClosedGate from './layout/AccountClosedGate';
import Logger from './common/logger';
import { config } from './config';
import './i18n/i18n';
import './index.css';
import { theme } from './material-ui/theme';
import SnackbarStack from './pages/common/SnackbarStack';
import { Auth0Provider } from './react-auth0-spa';
import TenantScope from './layout/TenantScope';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { StoreProvider } from './store/StoreProvider';

declare module '@mui/material/styles' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}


if (config.environment !== 'development') {
    Sentry.init({
        dsn: 'https://fd2f682ce44842ff994e1d5abb02540b@o445728.ingest.sentry.io/5447230',
        environment: config.environment,
        release: config.app.name + '@' + config.app.version,
    });
}

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
);

root.render(
    <StoreProvider>
        <StyledEngineProvider injectFirst={true}>
            <ThemeProvider theme={theme}>
                <Auth0Provider
                    domain={config.auth0.domain}
                    clientId={config.auth0.clientId}
                    // #1769 / ADR 0023: rotate refresh tokens instead of the
                    // silent-auth iframe, which browsers that block third-party
                    // cookies (Safari ITP, hardened Chrome) reject — logging
                    // users out on every reload and breaking token renewal
                    // mid-session. `localstorage` lets the session survive full
                    // page loads; explicit no-fallback keeps the dead-session
                    // path deterministic (clean re-login, not a hidden iframe
                    // retry that only works in cookie-permissive browsers).
                    // Requires the Auth0 app to allow the Refresh Token grant
                    // with rotation and the API to allow offline access — see
                    // the ADR before touching any of these four settings.
                    useRefreshTokens={true}
                    useRefreshTokensFallback={false}
                    cacheLocation={'localstorage'}
                    authorizationParams={{
                        audience: config.auth0.audience,
                        redirect_uri: window.location.origin,
                        scope: 'openid profile email offline_access',
                    }}
                >
                    {/* Remounts the app on every tenant switch, so no query hook
                        keeps reading the previous tenant's cache entry. */}
                    <TenantScope>
                        <AccountClosedGate>
                            <App />
                        </AccountClosedGate>
                    </TenantScope>
                </Auth0Provider>
                <SnackbarStack />
            </ThemeProvider>
        </StyledEngineProvider>
    </StoreProvider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(Logger('WebVitals').log);
